import { useCallback, useEffect, useRef, useState } from 'react';

import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';

export default function useSoldPriceAccess({ viewerSessionId, activeSlug, onAuthenticationRequired }) {
  const [revealedArtworks, setRevealedArtworks] = useState({});
  const [pendingSlug, setPendingSlug] = useState(null);
  const [plusPopupOpen, setPlusPopupOpen] = useState(false);
  const viewerRef = useRef(viewerSessionId);
  viewerRef.current = viewerSessionId;

  useEffect(() => {
    setRevealedArtworks({});
    setPendingSlug(null);
    setPlusPopupOpen(false);
  }, [viewerSessionId]);

  const acceptUnlockedArtwork = useCallback((slug, data, requestViewerId) => {
    if (viewerRef.current !== requestViewerId) return;
    if (data?.slug !== slug || data?.sold_price_access?.status !== 'unlocked') return;
    setRevealedArtworks((current) => ({
      ...current,
      [slug]: { ...data, _freshSoldPriceReveal: true },
    }));
  }, []);

  const unlockSoldPrice = useCallback(async (artwork) => {
    const slug = artwork?.slug;
    if (!slug || pendingSlug) return;
    const status = artwork?.sold_price_access?.status;
    if (status === 'sign_in' || !viewerSessionId) {
      onAuthenticationRequired?.();
      return;
    }
    if (status === 'plus_required') {
      setPlusPopupOpen(true);
      return;
    }
    if (status !== 'free_available') return;

    setPendingSlug(slug);
    try {
      const { response, data } = await fetchWrapper.post(
        `${config.apis.api.url}/artwork/${encodeURIComponent(slug)}/sold-price-reveal/`,
        { injectToken: true },
      );
      if (viewerRef.current !== viewerSessionId) return;
      if (response.ok) acceptUnlockedArtwork(slug, data, viewerSessionId);
      else if (response.status === 401) onAuthenticationRequired?.();
      else if (response.status === 403) setPlusPopupOpen(true);
    } catch (error) {
      console.error('[SoldPrice] Unable to unlock price', error);
    } finally {
      if (viewerRef.current === viewerSessionId) setPendingSlug(null);
    }
  }, [acceptUnlockedArtwork, onAuthenticationRequired, pendingSlug, viewerSessionId]);

  const refreshAfterPlus = useCallback(async () => {
    if (!activeSlug || !viewerSessionId) return;
    try {
      const { response, data } = await fetchWrapper.get(
        `${config.apis.api.url}/artwork/${encodeURIComponent(activeSlug)}/`,
        { injectToken: true },
      );
      if (response.ok) acceptUnlockedArtwork(activeSlug, data, viewerSessionId);
    } catch (error) {
      console.error('[SoldPrice] Unable to refresh after Plus activation', error);
    } finally {
      setPlusPopupOpen(false);
    }
  }, [acceptUnlockedArtwork, activeSlug, viewerSessionId]);

  return {
    revealedArtworks,
    pendingSlug,
    plusPopupOpen,
    closePlusPopup: () => setPlusPopupOpen(false),
    unlockSoldPrice,
    refreshAfterPlus,
  };
}
